// @flow

import type {MarkdocTagRegistration} from 'docs/markdoc';
import {Tag} from 'docs/util';
import {
  buildEntityDiagram,
  buildOverviewDiagram,
  buildSequenceDiagram,
  buildStateDiagram,
} from './util';

const formatError = ({id, message, type}) => ({
  id,
  level: 'critical',
  message: `[Error in ${type} diagram]: ${message}`,
});

export const Diagram: MarkdocTagRegistration = {
  tag: 'diagram',
  component: 'Diagram',
  attributes: {
    type: {
      type: String,
      default: 'sequence',
      matches: ['sequence', 'state', 'overview', 'entity'],
    },
    description: {
      type: String,
      required: true,
      localize: true,
      description:
        'A brief, one to two sentence overview about the content in this diagram. Used as a caption for assistive devices and as backup text to show if the diagram does not render.',
    },
  },
  validate(node, config) {
    const errors = [];
    const type = node.attributes.type;
    const elements = node.children;
    // Basic validations to check that diagrams only have node and edges as direct children
    if (!elements || !elements.length) {
      errors.push(
        formatError({
          type,
          id: 'missing-children',
          message: `Diagram does not have any children.`,
        }),
      );
    }

    if (elements.find((e) => e.type !== 'tag:node' && e.type !== 'tag:edge')) {
      errors.push(
        formatError({
          type,
          id: 'invalid-children',
          message: `Found a child that is neither a node nor an edge. Diagrams can only accept {% node %} and {% edge %} tags as children.`,
        }),
      );
    }

    if (elements.find((e) => e.type !== 'tag:node' && e.type !== 'tag:edge')) {
      errors.push(
        formatError({
          type,
          id: 'invalid-children',
          message: `Found a child that is neither a node nor an edge. Diagrams can only accept {% node %} and {% edge %} tags as children`,
        }),
      );
    }

    // Check all the nodes for duplicate IDs
    const nodes = elements.filter((e) => e.type === 'tag:node');
    const ids = [];
    const dupes = [];

    if (nodes.length < 2 && type !== 'entity') {
      errors.push(
        formatError({
          type,
          id: 'missing-node',
          message: `Does not have more than one {% node %} tag. All diagrams require at least two nodes and an edge.`,
        }),
      );
    }

    nodes.forEach((e) => {
      if (ids.includes(e.attributes.id)) {
        dupes.push(e.attributes.id);
      } else {
        ids.push(e.attributes.id);
      }
    });
    if (dupes.length) {
      errors.push(
        formatError({
          type,
          id: 'duplicate-node-id',
          message: `Found multiple nodes using the same id. Each node needs a unique id so we know where to draw the edges. Multiple nodes are using the ${
            dupes.length > 1 ? 'ids' : 'id'
          }: [${dupes.join(',')}]`,
        }),
      );
    }

    // Check all the edges to make sure they are pointing to valid nodes
    const edges = elements.filter((e) => e.type === 'tag:edge');
    if (!edges.length && type !== 'entity') {
      errors.push(
        formatError({
          type,
          id: 'missing-edge',
          message: `Does not have any {% edge %} tags. All diagrams require at least two nodes and an edge.`,
        }),
      );
    }

    const missingNodes = [];
    edges.forEach((e) => {
      if (!ids.includes(e.attributes.from)) {
        missingNodes.push(e.attributes.from);
      }
      if (!ids.includes(e.attributes.to)) {
        missingNodes.push(e.attributes.to);
      }
    });

    // TODO(adreyfus): Ensure referencing ids of child nodes for entity diagrams is valid
    if (missingNodes.length && type !== 'entity') {
      errors.push(
        formatError({
          type,
          id: 'incorrect-edge',
          message: `Found ${
            missingNodes.length
              ? 'an edge with an invalid id'
              : 'several edges with invalid ids'
          } set as a to/for attribute. Invalid id list: [${missingNodes.join(
            ',',
          )}]. Either change the edge to point to a valid node, or add a node with the proper id.`,
        }),
      );
    }

    return errors;
  },
  render(node, config) {
    const attributes = node.renderAttributes(this.attributes, config);
    const children = node.renderChildren(config);

    switch (attributes.type) {
      case 'sequence':
        attributes.elements = buildSequenceDiagram(attributes, children);
        break;
      case 'state':
        attributes.elements = buildStateDiagram(attributes, children);
        break;
      case 'overview':
        attributes.elements = buildOverviewDiagram(attributes, children);
        break;
      case 'entity':
        attributes.elements = buildEntityDiagram(attributes, children);
        break;
      default:
        attributes.elements = [];
    }
    return new Tag(this.tag, attributes);
  },
};
